import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useHistory, useLocation } from "react-router-dom";
import {
  IconMenu2,
  IconSearch,
  IconLogout,
  IconX,
  IconLayoutSidebarLeftCollapse,
  IconLayoutSidebarLeftExpand,
} from "@tabler/icons";
import { ToastHost, toast } from "../ui";
import { navFor, isNavActive, pageMeta } from "./nav";
import { getRoleId, getUsername, logout, roleName } from "./session";
import { roleHome } from "./roleHome";
import "./shell.css";

// The admin app frame: sidebar (248px, collapsible to a 72px icon rail, an
// off-canvas drawer below 992px), a sticky header (breadcrumb title, quick
// search, role, log out) and the content area. The root carries .pa so every
// page shares the design tokens.

const COLLAPSE_KEY = "pa.shell.collapsed";

const readFlag = (key) => {
  try {
    return localStorage.getItem(key) === "1";
  } catch (e) {
    return false;
  }
};
const writeFlag = (key, on) => {
  try {
    localStorage.setItem(key, on ? "1" : "0");
  } catch (e) {
    // storage unavailable: the preference just won't persist
  }
};

const initials = (name) =>
  String(name)
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

// Quick search: "48213" opens Order 360, "T1024" opens that payment row.
// Each target is role-guarded, so the box only offers what the role can open.
const canFindOrders = (roleId) => [5, 7, 8].includes(roleId);
const canFindRows = (roleId) => roleId === 7;

export function parseQuickSearch(text) {
  const q = String(text || "").trim();
  if (/^\d{1,11}$/.test(q)) return { kind: "order", id: q };
  const row = /^t[\s-]?(\d{1,11})$/i.exec(q);
  if (row) return { kind: "row", id: row[1] };
  return null;
}

function QuickSearch({ roleId, open, onOpenChange }) {
  const history = useHistory();
  const [q, setQ] = useState("");
  const inputRef = useRef(null);
  const rowsToo = canFindRows(roleId);

  useEffect(() => {
    if (open && inputRef.current) inputRef.current.focus();
  }, [open]);

  // "/" focuses the search from anywhere, except while typing in a field.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== "/" || e.ctrlKey || e.metaKey || e.altKey) return;
      const t = e.target;
      const tag = t && t.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || (t && t.isContentEditable)) return;
      if (document.querySelector(".modal.show, .pa-drawer-root")) return;
      e.preventDefault();
      onOpenChange(true);
      if (inputRef.current) inputRef.current.focus();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onOpenChange]);

  const close = () => {
    setQ("");
    onOpenChange(false);
    if (inputRef.current) inputRef.current.blur();
  };

  const submit = (e) => {
    e.preventDefault();
    const hit = parseQuickSearch(q);
    if (hit && hit.kind === "order") history.push(`/order-360?id=${hit.id}`);
    else if (hit && hit.kind === "row" && rowsToo) history.push(`/payment-table?tId=${hit.id}`);
    else {
      toast.info(rowsToo ? "Type an order ID (e.g. 48213) or a payment row like T1024" : "Type an order ID, e.g. 48213");
      return;
    }
    close();
  };

  const label = rowsToo ? "Find an order or a payment row" : "Find an order";
  return (
    <form role="search" className={`app-search ${open ? "is-open" : ""}`} onSubmit={submit} aria-label={label}>
      <label htmlFor="app-quick-search" className="pa-visually-hidden">
        {label}
      </label>
      <IconSearch className="app-search-icon" size={16} stroke={1.8} aria-hidden="true" />
      <input
        id="app-quick-search"
        ref={inputRef}
        type="search"
        inputMode="text"
        autoComplete="off"
        spellCheck={false}
        value={q}
        onChange={(e) => setQ(e.target.value)}
        onKeyDown={(e) => e.key === "Escape" && close()}
        placeholder={rowsToo ? "Order ID or T123" : "Order ID"}
        aria-keyshortcuts="/"
      />
      <kbd className="app-search-kbd" aria-hidden="true">
        /
      </kbd>
      <button type="button" className="app-icon-btn app-search-close" aria-label="Close search" onClick={close}>
        <IconX size={18} stroke={2} />
      </button>
    </form>
  );
}

export default function AppShell({ children }) {
  const location = useLocation();
  const history = useHistory();
  const roleId = getRoleId();
  const sections = navFor(roleId);
  const { section, title } = pageMeta(location.pathname, roleId);
  const name = roleName(roleId);
  const username = getUsername();
  const [collapsed, setCollapsed] = useState(() => readFlag(COLLAPSE_KEY));
  const [navOpen, setNavOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const mainRef = useRef(null);
  const sidebarRef = useRef(null);

  // Any navigation (also a query-only change such as /order-360?id=5 ->
  // /order-360) closes the mobile menu and the search; a new page scrolls to the top.
  useEffect(() => {
    setNavOpen(false);
    setSearchOpen(false);
  }, [location.key, location.pathname, location.search]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  useEffect(() => {
    document.title = title ? `${title} · Demandeals Admin` : "Demandeals Admin";
  }, [title]);

  // The mobile drawer: Esc closes it, focus moves into it, and it closes by
  // itself when the window grows to the desktop layout.
  useEffect(() => {
    if (!navOpen) return undefined;
    const onKey = (e) => e.key === "Escape" && setNavOpen(false);
    document.addEventListener("keydown", onKey);
    const link = sidebarRef.current && sidebarRef.current.querySelector(".app-nav-link");
    if (link) link.focus();
    return () => document.removeEventListener("keydown", onKey);
  }, [navOpen]);

  useEffect(() => {
    if (!window.matchMedia) return undefined;
    const mq = window.matchMedia("(min-width: 992px)");
    const onChange = (e) => e.matches && setNavOpen(false);
    if (mq.addEventListener) mq.addEventListener("change", onChange);
    else mq.addListener(onChange);
    return () => (mq.removeEventListener ? mq.removeEventListener("change", onChange) : mq.removeListener(onChange));
  }, []);

  const toggleCollapsed = () =>
    setCollapsed((c) => {
      writeFlag(COLLAPSE_KEY, !c);
      return !c;
    });

  const signOut = () => {
    logout();
    history.replace("/pages/login");
  };

  const onSearchOpenChange = useCallback((open) => setSearchOpen(open), []);
  // A menu tap closes the mobile drawer even when it opens the page already
  // shown (the location may not change at all).
  const closeNav = () => setNavOpen(false);
  const canSearch = canFindOrders(roleId);

  return (
    <div className={`pa app-shell ${collapsed ? "is-collapsed" : ""} ${navOpen ? "is-nav-open" : ""} ${searchOpen ? "is-search-open" : ""}`}>
      <a
        className="app-skip"
        href="#app-content"
        onClick={(e) => {
          e.preventDefault();
          if (mainRef.current) mainRef.current.focus();
        }}
      >
        Skip to content
      </a>

      <aside id="app-sidebar" ref={sidebarRef} className="app-sidebar" aria-label="Main menu">
        <div className="app-brand">
          <Link to={roleHome(roleId)} className="app-brand-link" aria-label="Demandeals admin home" onClick={closeNav}>
            <span className="app-brand-mark" aria-hidden="true">
              D
            </span>
            <span className="app-brand-text">
              <span className="app-brand-name">Demandeals</span>
              <span className="app-brand-sub">Admin console</span>
            </span>
          </Link>
          <button type="button" className="app-icon-btn app-nav-close" aria-label="Close menu" onClick={() => setNavOpen(false)}>
            <IconX size={18} stroke={2} />
          </button>
        </div>

        <nav className="app-nav" aria-label="Main">
          {sections.map((s) => (
            <div key={s.title} className="app-nav-section">
              <div className="app-nav-heading">{s.title}</div>
              <ul>
                {s.items.map((it) => {
                  const active = isNavActive(it, location.pathname);
                  const Icon = it.icon;
                  return (
                    <li key={it.to}>
                      <Link
                        to={it.to}
                        className={`app-nav-link ${active ? "is-active" : ""}`}
                        aria-current={active ? "page" : undefined}
                        title={collapsed ? it.label : undefined}
                        onClick={closeNav}
                      >
                        <Icon className="app-nav-icon" size={20} stroke={1.7} aria-hidden="true" />
                        <span className="app-nav-label">{it.label}</span>
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </nav>

        <div className="app-sidebar-foot">
          <div className="app-user" title={collapsed ? `Signed in as ${name}` : undefined}>
            <span className="app-avatar" aria-hidden="true">
              {initials(name)}
            </span>
            <span className="app-user-text">
              <span className="app-user-name">{name}</span>
              <span className="app-user-sub">Signed in</span>
            </span>
          </div>
          <button
            type="button"
            className="app-icon-btn app-collapse"
            onClick={toggleCollapsed}
            aria-label={collapsed ? "Expand the menu" : "Collapse the menu"}
            title={collapsed ? "Expand the menu" : "Collapse the menu"}
          >
            {collapsed ? <IconLayoutSidebarLeftExpand size={20} stroke={1.7} /> : <IconLayoutSidebarLeftCollapse size={20} stroke={1.7} />}
          </button>
        </div>
      </aside>

      {navOpen && <div className="app-backdrop" onClick={() => setNavOpen(false)} aria-hidden="true" />}

      <div className="app-main">
        <header className="app-header">
          <button
            type="button"
            className="app-icon-btn app-menu-btn"
            aria-label="Open menu"
            aria-controls="app-sidebar"
            aria-expanded={navOpen}
            onClick={() => setNavOpen(true)}
          >
            <IconMenu2 size={20} stroke={1.8} />
          </button>

          <nav className="app-title" aria-label="Breadcrumb">
            <ol>
              {section && <li className="app-title-section">{section}</li>}
              <li className="app-title-text" aria-current="page">
                {title || "Demandeals Admin"}
              </li>
            </ol>
          </nav>

          <div className="app-header-end">
            {canSearch && <QuickSearch roleId={roleId} open={searchOpen} onOpenChange={onSearchOpenChange} />}
            {canSearch && (
              <button type="button" className="app-icon-btn app-search-toggle" aria-label="Search orders" onClick={() => setSearchOpen(true)}>
                <IconSearch size={20} stroke={1.8} />
              </button>
            )}
            <span className="app-role-chip" title={name}>
              <span className="app-role-dot" aria-hidden="true" />
              {username || name}
            </span>
            <button type="button" className="app-logout" onClick={signOut} aria-label="Log out" title="Log out">
              <IconLogout size={18} stroke={1.8} aria-hidden="true" />
              <span className="app-logout-text">Log out</span>
            </button>
          </div>
        </header>

        <main id="app-content" ref={mainRef} tabIndex={-1} className="app-content">
          {children}
        </main>
      </div>

      <ToastHost primary />
    </div>
  );
}
